import { ComponentInteractionType, SceneComponent } from '@/_models/virtualStaging/Scene.component';
import { Inputs, Outputs } from '@/_models/virtualStaging/BIM/component/types';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { createMenuItem } from '@/_models/virtualStaging/BIM/utils';
import { AnimatedPipe } from '@/_models/virtualStaging/BIM/AnimatedPipe';
import { segmentToPipeMap } from '@/_models/virtualStaging/BIM/component/data';

export class BIMComponent extends SceneComponent {
  public root: any = null;

  public pivot: any = null;

  private itemsMap = new Map();

  public BMListItems = [];

  public animatedPipes = new Map();

  inputs: Inputs = {
    url: '../../BIM.fbx',
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    visible: true,
    colorDefault: 'grey',
    colorHover: 'red',
    colorClick: 'red',
    opacityDefault: 0,
    opacityHover: 0.5,
    opacityClick: 0.5,
  };

  outputs: Outputs = {
    loadingState: LoadingState.Idle,
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  declare context: any;

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.root.position.set(this.inputs.position.x, this.inputs.position.y, this.inputs.position.z);
    this.root.visible = this.inputs.visible;
    this.root.scale.set(this.inputs.scale.x, this.inputs.scale.y, this.inputs.scale.z);
    this.pivot = new THREE.Object3D();
    this.root.add(this.pivot);
    this.root.rotation.set(this.inputs.rotation.x, this.inputs.rotation.y, this.inputs.rotation.z);
    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;
    this.outputs.loadingState = LoadingState.Loading;
    const textureLoader = new THREE.TextureLoader();
    this.initModel().then(async (model) => {
      await AnimatedPipe.preloadTextures(textureLoader);
      const floors = [];
      model.traverse((obj) => {
        // if (obj.type !== 'Group') obj.visible = false;
        if (obj.name.startsWith('Floor')) floors.push(obj);
        if (obj.isMesh && obj.material) {
          this.fixMaterial(obj);
        }
        if (obj.isMesh) {
          obj.visible = true;
          this.itemsMap.set(obj.id, obj);
        }
        if (obj.name === 'Pipe_Types_Standard_2302961') {
          this.animatedPipes.set(
            obj.id,
            new AnimatedPipe(
              obj,
              {
                textureRotation: -Math.PI / 2,
                offsetSpeedX: 0.0003,
                offsetSpeedY: 0,
              },
              this.context,
            ),
          );
        }
        if (obj.name === 'Pipe_Types_Standard_2302977') {
          this.animatedPipes.set(
            obj.id,
            new AnimatedPipe(
              obj,
              {
                textureRotation: Math.PI / 2,
                offsetSpeedX: 0.0003,
                offsetSpeedY: 0,
              },
              this.context,
            ),
          );
        }
        if (obj.name === 'Pipe_Types_Standard_2177850') {
          this.animatedPipes.set(
            obj.id,
            new AnimatedPipe(
              obj,
              {
                textureRotation: Math.PI / 2,
                offsetSpeedX: 0.0003,
                offsetSpeedY: 0,
              },
              this.context,
            ),
          );
        }
        if (obj.name === 'Pipe_Types_Standard_2177264') {
          this.animatedPipes.set(
            obj.id,
            new AnimatedPipe(
              obj,
              {
                textureRotation: Math.PI / 2,
                offsetSpeedX: 0.0003,
                offsetSpeedY: 0,
                textureRepeat: { x: 1, y: 3 },
              },
              this.context,
            ),
          );
        }
        if (obj.name === 'Pipe_Types_Standard_2024792') {
          this.animatedPipes.set(
            obj.id,
            new AnimatedPipe(
              obj,
              {
                textureRotation: Math.PI / 2,
                offsetSpeedX: 0.0003,
                offsetSpeedY: 0,
                textureRepeat: { x: 1, y: 3 },
              },
              this.context,
            ),
          );
        }
        if (obj.name === 'Pipe_Types_Standard_2024670') {
          this.animatedPipes.set(
            obj.id,
            new AnimatedPipe(
              obj,
              {
                textureRotation: -Math.PI / 2,
                offsetSpeedX: 0.0003,
                offsetSpeedY: 0,
                textureRepeat: { x: 1, y: 4 },
              },
              this.context,
            ),
          );
        }
        if (obj.name === 'Pipe_Types_Standard_2202212') {
          this.animatedPipes.set(
            obj.id,
            new AnimatedPipe(
              obj,
              {
                textureRotation: -Math.PI / 2,
                offsetSpeedX: 0.0003,
                offsetSpeedY: 0,
                textureRepeat: { x: 1, y: 4 },
              },
              this.context,
            ),
          );
        }
      });

      floors.forEach((floor) => {
        floor.parent.remove(floor);
      });

      this.itemsMap.forEach((pipe, index) => {
        const item = createMenuItem({ id: index, name: pipe.name, itemId: pipe.id });
        this.BMListItems.push(item);
      });
      this.outputs.loadingState = LoadingState.Loaded;
    });
  }

  async initModel() {
    const { url } = this.inputs;
    const model = await this.loadAsync(url);
    model.rotation.z = -Math.PI / 2;
    model.rotation.x = -Math.PI / 2;
    this.root.add(model);

    return model;
  }

  getItem(id) {
    return this.itemsMap.get(id);
  }

  setItemColor(id, color) {
    this.itemsMap.get(id).material.color.set(color);
  }

  setItemOpacity(id, opacity) {
    this.itemsMap.get(id).material.opacity = opacity;
  }

  setAnimatedPipeOpacity(id, opacity) {
    const animatedPipe = this.animatedPipes.get(id);
    if (!animatedPipe) return;
    animatedPipe.obj3D.material.opacity = opacity;
  }

  getSegmentsOfPipe(segmentName) {
    const pipe = segmentToPipeMap.get(segmentName);
    if (!pipe) return [];
    return [...pipe.segments.map((s) => s.name), ...pipe.animatedSegments.map((s) => s.name)];
  }

  getSegmentIdByName(name) {
    let segmentId = null;
    this.itemsMap.forEach((segment, id) => {
      if (segment.name === name) {
        segmentId = id;
      }
    });
    return segmentId;
  }

  fixMaterial(mesh) {
    const THREE = this.context.three;
    mesh.material.dispose();
    mesh.material = new THREE.MeshBasicMaterial({
      color: this.inputs.colorDefault,
      transparent: true,
      opacity: this.inputs.opacityDefault,
      depthTest: false,
      depthWrite: false,
    });
  }

  onTick(tickDelta: number) {
    this.animatedPipes.forEach((pipe) => {
      const arrowSpeedX = pipe.inputs.offsetSpeedX;
      const arrowSpeedY = pipe.inputs.offsetSpeedY;
      const arrowOffset = pipe.arrowMaterial.map.offset;
      arrowOffset.x -= arrowSpeedX * tickDelta;
      arrowOffset.y -= arrowSpeedY * tickDelta;
      pipe.arrowMaterial.map.offset = arrowOffset;
    });
  }

  private loadAsync = async (url: string) => {
    const THREE = this.context.three;
    if (url.includes('.gltf') || url.includes('.glb'))
      return new Promise<any>((resolve, reject) => {
        const loader = new THREE.GLTFLoader();
        loader.load(
          url,
          (gltf) => resolve(gltf),
          () => null,
          (err) => reject(err),
        );
      });
    if (url.includes('.fbx'))
      return new Promise<any>((resolve, reject) => {
        const loader = new THREE.FBXLoader();
        loader.load(
          url,
          (model) => resolve(model),
          () => null,
          (err) => reject(err),
        );
      });
    if (url.includes('.png') || url.includes('.jpg') || url.includes('base64'))
      return new Promise<any>((resolve, reject) => {
        const loader = new THREE.TextureLoader();
        loader.load(
          url,
          (texture) => resolve(texture),
          () => null,
          (err) => reject(err),
        );
      });
  };
}

export const BIMComponentType = 'mp.BIMComponent';
export const makeBIMComponent = (): any => new BIMComponent();
